import { CommonModule } from "@angular/common";
import { Component, Input, forwardRef } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { MatNativeDateModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { UiIconComponent } from "../ui-icon/ui-icon.component";

let nextId = 0;

/**
 * A date field that keeps our own floating-label field shell (so it can't
 * develop the label-through-border bug mat-form-field had) but hands the
 * actual picking UI to Angular Material's calendar popup, which is what was
 * asked for back. MatDatepickerInput/MatDatepickerToggle don't require
 * mat-form-field — they attach to a plain `<input>` — so the two combine
 * fine. The public contract is still a plain 'YYYY-MM-DD' string, same as
 * every other date field in the app; the Date <-> string conversion the
 * calendar needs happens inside this component only.
 */
@Component({
    selector: "ui-date-input",
    standalone: true,
    imports: [CommonModule, MatDatepickerModule, MatNativeDateModule, UiIconComponent],
    templateUrl: "./ui-date-input.component.html",
    styleUrls: ["./ui-date-input.component.scss"],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UiDateInputComponent),
            multi: true,
        },
    ],
})
export class UiDateInputComponent implements ControlValueAccessor {
    @Input() label = "";
    @Input() hint = "";
    @Input() errorText = "";
    @Input() required = false;
    @Input() min: Date | null = null;
    @Input() max: Date | null = null;

    readonly id = `ui-date-input-${nextId++}`;

    dateValue: Date | null = null;
    focused = false;
    disabled = false;

    private onChange: (value: string | null) => void = () => {};
    private onTouched: () => void = () => {};

    onDateChange(date: Date | null): void {
        this.dateValue = date;
        this.onChange(this.toIsoDate(date));
    }

    onFocus(): void {
        this.focused = true;
    }

    onBlur(): void {
        this.focused = false;
        this.onTouched();
    }

    writeValue(value: string | Date | null): void {
        if (!value) {
            this.dateValue = null;
        } else if (value instanceof Date) {
            this.dateValue = value;
        } else {
            // Parse 'YYYY-MM-DD' as a local date, not UTC — new Date('YYYY-MM-DD')
            // parses as UTC midnight, which can render as the previous day in
            // any timezone west of UTC.
            const [y, m, d] = value.split("-").map(Number);
            this.dateValue = y && m && d ? new Date(y, m - 1, d) : null;
        }
    }

    registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    private toIsoDate(date: Date | null): string | null {
        if (!date) {
            return null;
        }
        const y = date.getFullYear();
        const m = `${date.getMonth() + 1}`.padStart(2, "0");
        const d = `${date.getDate()}`.padStart(2, "0");
        return `${y}-${m}-${d}`;
    }
}
